import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Edit2, Trash2, Loader2, RefreshCw, X, GripVertical,
  Footprints, Shirt, Gem, LayoutGrid, ShoppingBag, Tag, Package,
  Watch, Glasses, Dumbbell, Trophy, Star, Heart, Zap, Crown,
  Layers, Umbrella, Briefcase, Sparkles,
} from 'lucide-react';
import { api } from '../api/client';

// ─── Icon registry ─────────────────────────────────────────────────────────────

const ICON_OPTIONS = [
  { name: 'Footprints', Icon: Footprints },
  { name: 'Shirt', Icon: Shirt },
  { name: 'Gem', Icon: Gem },
  { name: 'LayoutGrid', Icon: LayoutGrid },
  { name: 'ShoppingBag', Icon: ShoppingBag },
  { name: 'Tag', Icon: Tag },
  { name: 'Package', Icon: Package },
  { name: 'Watch', Icon: Watch },
  { name: 'Glasses', Icon: Glasses },
  { name: 'Dumbbell', Icon: Dumbbell },
  { name: 'Trophy', Icon: Trophy },
  { name: 'Star', Icon: Star },
  { name: 'Heart', Icon: Heart },
  { name: 'Zap', Icon: Zap },
  { name: 'Crown', Icon: Crown },
  { name: 'Layers', Icon: Layers },
  { name: 'Umbrella', Icon: Umbrella },
  { name: 'Briefcase', Icon: Briefcase },
  { name: 'Sparkles', Icon: Sparkles },
] as const;

type IconName = typeof ICON_OPTIONS[number]['name'];

function IconComponent({ name, className }: { name: string; className?: string }) {
  const found = ICON_OPTIONS.find(o => o.name === name);
  if (!found) return <Tag className={className} />;
  const { Icon } = found;
  return <Icon className={className} />;
}

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  imageUrl: string | null;   // stores icon name
  sortOrder: number;
  isActive: boolean;
}

// ─── Component ─────────────────────────────────────────────────────────────────

export const CategoryManager: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<string[]>([]);

  // Form
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState<IconName>('Tag');
  const [sortOrder, setSortOrder] = useState('0');
  const [isActive, setIsActive] = useState(true);

  // ── Fetch ───────────────────────────────────────────────────────────────────

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<Category[]>('/api/v1/categories');
      setCategories(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // ── Form helpers ────────────────────────────────────────────────────────────

  const resetForm = () => {
    setName(''); setDescription(''); setIcon('Tag');
    setSortOrder('0'); setIsActive(true); setFormErrors([]);
  };

  const openAdd = () => { resetForm(); setEditing(null); setIsOpen(true); };

  const openEdit = (cat: Category) => {
    setEditing(cat);
    setName(cat.name);
    setDescription(cat.description ?? '');
    setIcon((cat.imageUrl as IconName) ?? 'Tag');
    setSortOrder(cat.sortOrder.toString());
    setIsActive(cat.isActive);
    setFormErrors([]);
    setIsOpen(true);
  };

  const handleDelete = async (cat: Category) => {
    if (!window.confirm(`¿Eliminar "${cat.name}"?`)) return;
    try {
      await api.delete(`/api/v1/categories/${cat.id}`);
      setCategories(prev => prev.filter(c => c.id !== cat.id));
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error al eliminar');
    }
  };

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: string[] = [];
    if (name.trim().length < 2) errs.push('Nombre: mínimo 2 caracteres.');
    if (errs.length) { setFormErrors(errs); return; }

    setSaving(true);
    setFormErrors([]);

    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      imageUrl: icon,
      sortOrder: Number(sortOrder) || 0,
      isActive,
    };

    try {
      if (editing) {
        const res = await api.put<Category>(`/api/v1/categories/${editing.id}`, payload);
        setCategories(prev => prev.map(c => c.id === editing.id ? res.data : c));
      } else {
        const res = await api.post<Category>('/api/v1/categories', payload);
        setCategories(prev => [...prev, res.data]);
      }
      setIsOpen(false);
    } catch (e) {
      setFormErrors([e instanceof Error ? e.message : 'Error al guardar']);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center bg-page-surface p-6 rounded-2xl border border-page-border gap-4">
        <div>
          <span className="text-xs font-semibold text-brand-primary uppercase tracking-widest">Catálogo</span>
          <h2 className="font-heading font-bold text-2xl text-ink mt-0.5">Categorías</h2>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchCategories}
            disabled={loading}
            className="p-3 bg-white text-ink-muted rounded-xl hover:bg-page-surface transition-all border border-page-border disabled:opacity-50"
            title="Actualizar"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 bg-brand-primary hover:bg-brand-hover text-white font-bold px-5 py-3 rounded-xl shadow-sm shadow-brand-glow transition-all text-sm"
          >
            <Plus className="w-4 h-4" /> Nueva Categoría
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3.5 rounded-xl text-xs flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-rose-500 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex flex-col justify-center items-center py-20 bg-page-surface rounded-2xl border border-page-border">
          <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
          <p className="text-ink-muted text-sm mt-3">Cargando categorías...</p>
        </div>
      )}

      {/* Empty */}
      {!loading && categories.length === 0 && (
        <div className="text-center py-16 text-ink-faint bg-page-surface rounded-2xl border border-page-border">
          <LayoutGrid className="w-10 h-10 mx-auto mb-3 text-ink-faint" />
          <p className="text-sm">No hay categorías. Creá la primera.</p>
        </div>
      )}

      {/* Grid */}
      {!loading && categories.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(cat => (
              <div
                key={cat.id}
                className="bg-white border border-page-border rounded-2xl p-5 flex items-center gap-4 shadow-sm hover:border-brand-border transition-colors group"
              >
                {/* Drag handle (visual only) */}
                <GripVertical className="w-4 h-4 text-ink-faint flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab" />

                {/* Icon */}
                <div className="w-12 h-12 rounded-xl bg-brand-surface border border-brand-border flex items-center justify-center flex-shrink-0">
                  <IconComponent name={cat.imageUrl ?? 'Tag'} className="w-5 h-5 text-brand-primary" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-ink text-sm truncate">{cat.name}</h4>
                    {!cat.isActive && (
                      <span className="text-[9px] bg-amber-50 border border-amber-200 text-amber-600 px-1.5 py-0.5 rounded font-bold">
                        Inactiva
                      </span>
                    )}
                  </div>
                  {cat.description && (
                    <p className="text-[11px] text-ink-muted mt-0.5 truncate">{cat.description}</p>
                  )}
                  <p className="text-[10px] text-ink-faint mt-0.5">Orden: {cat.sortOrder}</p>
                </div>

                {/* Actions */}
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => openEdit(cat)}
                    className="p-2 text-ink-faint hover:text-brand-primary hover:bg-brand-surface rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(cat)}
                    className="p-2 text-ink-faint hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex justify-center items-center p-4">
          <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm animate-fade-in" onClick={() => setIsOpen(false)} />

          <form
            onSubmit={handleSubmit}
            className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-page-border flex flex-col animate-fade-scale"
          >
            {/* Header */}
            <div className="p-5 border-b border-page-border flex justify-between items-center flex-shrink-0">
              <h3 className="font-heading font-bold text-lg text-ink">
                {editing ? 'Editar Categoría' : 'Nueva Categoría'}
              </h3>
              <button type="button" onClick={() => setIsOpen(false)} className="text-ink-faint hover:text-ink p-1.5 hover:bg-page-surface rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-5 overflow-y-auto">

              {formErrors.length > 0 && (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 p-3 rounded-xl space-y-1 text-xs">
                  {formErrors.map((e, i) => <div key={i} className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-rose-500 rounded-full" />{e}</div>)}
                </div>
              )}

              {/* Nombre */}
              <Field label="Nombre">
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Ej. Calzados, Remeras..."
                  className={inputCls}
                  required
                />
              </Field>

              {/* Descripción */}
              <Field label="Descripción (opcional)">
                <input
                  type="text"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Descripción breve para el frontend"
                  className={inputCls}
                />
              </Field>

              {/* Icono */}
              <Field label="Icono">
                <div className="grid grid-cols-8 gap-1.5 p-3 bg-page-surface border border-page-border rounded-xl">
                  {ICON_OPTIONS.map(({ name: iName, Icon }) => (
                    <button
                      key={iName}
                      type="button"
                      onClick={() => setIcon(iName)}
                      title={iName}
                      className={`p-2 rounded-lg flex items-center justify-center transition-all ${icon === iName
                        ? 'bg-brand-primary text-white shadow-sm'
                        : 'bg-white border border-page-border text-ink-muted hover:text-ink hover:border-brand-border'
                        }`}
                    >
                      <Icon className="w-4 h-4" />
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-ink-faint mt-1.5">Seleccionado: <strong>{icon}</strong></p>
              </Field>

              {/* Sort order + Activa */}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Orden (menor = primero)">
                  <input
                    type="number"
                    min="0"
                    value={sortOrder}
                    onChange={e => setSortOrder(e.target.value)}
                    className={inputCls}
                  />
                </Field>
                <Field label="Estado">
                  <button
                    type="button"
                    onClick={() => setIsActive(v => !v)}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all ${isActive
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-page-surface border-page-border text-ink-muted'
                      }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-ink-faint'}`} />
                    {isActive ? 'Activa' : 'Inactiva'}
                  </button>
                </Field>
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-page-border flex gap-3 justify-end flex-shrink-0">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="px-5 py-2.5 bg-page-surface text-ink-muted rounded-xl hover:bg-page-panel transition-colors text-sm font-medium border border-page-border"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2.5 bg-brand-primary hover:bg-brand-hover disabled:opacity-50 text-white font-bold rounded-xl shadow-sm shadow-brand-glow transition-all text-sm flex items-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? 'Guardar cambios' : 'Crear categoría'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// ─── Sub-components ─────────────────────────────────────────────────────────────

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider mb-1.5">{label}</label>
    {children}
  </div>
);

const inputCls = 'w-full bg-white border border-page-border rounded-xl px-4 py-2.5 text-ink placeholder-ink-faint focus:outline-none focus:border-brand-primary/50 focus:ring-1 focus:ring-brand-glow transition-colors text-sm';
